import type { NavigateOptions } from 'react-router-dom';

import { persistWorkspaceSelection } from '../helpers/persist-workspace-selection';
import { resetAccountScopedStores } from './reset-account-scoped-stores';
import { resolveWorkspaceDestination } from '../helpers/resolve-workspace-destination';
import {
  WorkspaceSwitchOutcome,
} from '@/contexts/account/types/workspace-switch-outcome-enums';
import type {
  WorkspaceSwitchResult,
} from '@/contexts/account/types/workspace-switch-outcome-types';

import { prefetchAccountData } from '@/features/workspace/api/prefetch-account-data';
import * as Logger from '@/lib/monitoring/logger';
import { clearAccountScopedQueries } from '@/lib/query/cache-invalidation';
import { AccountStorage } from '@/features/workspace/api/account-storage/account-storage';
import {
  ExecuteWorkspaceSwitchDeps,
} from '@/contexts/account/types/execute-workspace-switch-types';

export async function executeWorkspaceSwitch(
  targetWorkspaceId: string,
  deps: ExecuteWorkspaceSwitchDeps
): Promise<WorkspaceSwitchResult> {
  const {
    accountsList,
    currentAccountId,
    roles,
    currentUserEmail,
    queryClient,
    navigate,
    beginTransition,
    commitWorkspace,
    endTransition,
    onRequiresPlan,
    userId,
    navigateState,
    preserveLocation = false,
  } = deps;

  if (currentAccountId === targetWorkspaceId) {
    return {
      outcome: WorkspaceSwitchOutcome.NO_OP,
      workspaceId: targetWorkspaceId,
    };
  }

  const targetAccount = accountsList.find(account => account.id === targetWorkspaceId);
  if (!targetAccount) {
    Logger.warn('Workspace switch failed: target not found', {
      targetWorkspaceId,
      currentAccountId,
    });
    return {
      outcome: WorkspaceSwitchOutcome.FAILED,
      workspaceId: targetWorkspaceId,
      errorMessage: `Workspace ${targetWorkspaceId} not found`,
    };
  }

  const previousAccountId = currentAccountId;
  const targetPixel = targetAccount.pixels?.[0] ?? null;
  const destination = resolveWorkspaceDestination(targetAccount);

  beginTransition(previousAccountId);

  // Yield so React can paint the disabled/loading UI before commit.
  await Promise.resolve();

  try {
    persistWorkspaceSelection(targetWorkspaceId, targetAccount);
    resetAccountScopedStores(targetAccount);
  } catch (error) {
    endTransition();
    Logger.error(
      'Workspace switch failed before commit',
      error instanceof Error ? error : null,
      {
        errorMessage: error instanceof Error ? error.message : String(error),
        targetWorkspaceId,
        previousAccountId,
      }
    );
    return {
      outcome: WorkspaceSwitchOutcome.FAILED,
      workspaceId: targetWorkspaceId,
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  }

  clearAccountScopedQueries(queryClient);

  commitWorkspace({
    accountId: targetWorkspaceId,
    account: targetAccount,
    pixel: targetPixel,
    previousAccountId,
  });

  if (destination.requiresPlanOrRecovery && onRequiresPlan && userId) {
    onRequiresPlan(userId);
  }

  if (!preserveLocation) {
    const navigateOptions: NavigateOptions = { replace: true };
    if (navigateState !== undefined) {
      navigateOptions.state = navigateState;
    }

    try {
      await navigate(destination.path, navigateOptions);
    } catch (error) {
      // Context already committed — do not roll back browser history or workspace.
      Logger.error(
        'Workspace switch navigation failed after commit',
        error instanceof Error ? error : null,
        {
          errorMessage: error instanceof Error ? error.message : String(error),
          targetWorkspaceId,
          destinationPath: destination.path,
        }
      );
      endTransition();
      return {
        outcome: WorkspaceSwitchOutcome.FAILED,
        workspaceId: targetWorkspaceId,
        destinationPath: destination.path,
        errorMessage: error instanceof Error ? error.message : String(error),
      };
    }
  }

  if (targetPixel?.id) {
    void prefetchAccountData({
      queryClient,
      account: targetAccount,
      pixelId: targetPixel.id,
      roles,
      currentUserEmail,
    });
  }

  AccountStorage.pruneOldAccounts(5);
  endTransition();

  return {
    outcome: destination.requiresPlanOrRecovery
      ? WorkspaceSwitchOutcome.REQUIRES_PLAN_OR_RECOVERY
      : WorkspaceSwitchOutcome.SWITCHED,
    workspaceId: targetWorkspaceId,
    destinationPath: preserveLocation ? undefined : destination.path,
  };
}
